import React, { useEffect } from 'react';
import { Box, Typography, Paper, Chip, Button } from '@mui/material';
import { Plus } from 'lucide-react';

import ResponsiveTable, { ActionMenu } from './ResponsiveTable';
import FullPageLoader from './FullPageLoader';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import FormDialog from '../Forms/FormDialog';
import { usePermissions } from '../../contexts/PermissionContext';
import useCRUDPage from '../../hooks/useCRUDPage';

/**
 * CRUDPage - Universal template component for CRUD pages
 * 
 * Replaces 600+ line CRUD pages with 80-line configuration
 * 
 * Features:
 * - Responsive data table with search, sort, pagination
 * - Create, Update, Delete operations
 * - Form dialog with validation
 * - Delete confirmation modal
 * - Permission-based action buttons
 * - Export to CSV
 * - Loading states
 * - Custom renderers for table cells
 * 
 * @param {Object} props
 * @param {string} props.title - Page title (e.g., 'Site Management')
 * @param {string} props.subtitle - Page subtitle (e.g., 'Manage all sites')
 * @param {Object} props.icon - Icon component for header
 * @param {Array} props.columns - Table column definitions
 * @param {Array} props.fields - Form field definitions
 * @param {Array} props.searchFields - Fields to search in (e.g., ['name', 'code', 'email'])
 * @param {Object} props.permissions - Permission configuration
 * @param {string} props.permissions.resource - Resource name for permissions (e.g., 'Site')
 * @param {string} props.permissions.module - Module name for permissions (e.g., 'siteManagement')
 * @param {Object} props.crudConfig - Configuration for useCRUDPage hook
 * @param {Function} props.renderCustomActions - Optional custom actions renderer
 * @param {Function} props.onView - Optional custom view handler
 * @param {Object} props.headerActions - Optional additional header actions
 * @param {Function} props.beforeSubmit - Optional function to run before form submit
 * @param {number} props.refreshSignal - Optional counter; increment to force a data refetch
 */
const CRUDPage = ({
  title,
  subtitle,
  icon: Icon,
  columns,
  fields,
  searchFields = [],
  permissions = {},
  crudConfig = {},
  renderCustomActions,
  onView,
  headerActions,
  beforeSubmit,
  emptyMessage,
  refreshSignal,
}) => {
  const { can } = usePermissions();

  // Extract permission config
  const { resource, module: permissionModule } = permissions;

  // Check permissions (also check disableCreate from crudConfig)
  const canCreate = crudConfig.disableCreate ? false : (resource && permissionModule ? can(resource, permissionModule, 'create') : true);
  const canEdit = resource && permissionModule ? can(resource, permissionModule, 'update') : true;
  const canDelete = resource && permissionModule ? can(resource, permissionModule, 'delete') : true;

  // Use CRUD hook
  const {
    data,
    loading,
    searchTerm,
    formOpen,
    editing,
    deleteModalOpen,
    itemToDelete,
    setSearchTerm,
    setFormOpen,
    setDeleteModalOpen,
    handleCreate,
    handleEdit,
    handleDelete,
    handleView: defaultHandleView,
    handleFormSubmit,
    confirmDelete,
    handleExport,
    handleRefresh,
    getFilteredData,
  } = useCRUDPage(crudConfig);

  // Allow parents to force a refetch (e.g. after saving data outside the CRUD form)
  useEffect(() => {
    if (refreshSignal) handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // Get filtered data
  const filteredData = getFilteredData(searchFields);

  // Wrap form submit with beforeSubmit if provided
  const wrappedHandleFormSubmit = async (formData) => {
    if (beforeSubmit) {
      const result = await beforeSubmit(formData, editing);
      if (result === false) return; // Cancel submit
      if (result && typeof result === 'object') {
        // Use transformed data
        return handleFormSubmit(result);
      }
    }
    return handleFormSubmit(formData);
  };

  // Use custom view handler if provided
  const handleViewItem = onView || defaultHandleView;

  // Enhance columns with action menu
  const enhancedColumns = columns.map(col => {
    if (col.field === 'actions') {
      return {
        ...col,
        renderCell: renderCustomActions 
          ? (cellParams) => renderCustomActions({
              ...cellParams,
              onEdit: canEdit ? () => handleEdit(cellParams.row) : undefined,
              onDelete: canDelete ? () => handleDelete(cellParams.row) : undefined,
              onView: onView ? () => handleViewItem(cellParams.row) : undefined,
              canEdit,
              canDelete
            })
          : (({ row }) => (
              <ActionMenu
                rowData={row}
                onView={onView ? () => handleViewItem(row) : undefined}
                onEdit={canEdit ? () => handleEdit(row) : undefined}
                onDelete={canDelete ? () => handleDelete(row) : undefined}
              />
            ))
      };
    }
    return col;
  });

  if (loading && data.length === 0) {
    return <FullPageLoader message={`Loading ${crudConfig.entityNamePlural?.toLowerCase() || 'data'}...`} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Data Table with integrated header */}
      <ResponsiveTable
        title={title}
        subtitle={`${filteredData.length} ${filteredData.length === 1 ? crudConfig.entityName || 'item' : crudConfig.entityNamePlural || 'items'} ${subtitle ? '• ' + subtitle : ''}`}
        columns={enhancedColumns}
        data={filteredData}
        loading={loading}
        onSearch={setSearchTerm}
        onRefresh={handleRefresh}
        onExport={() => handleExport(columns, filteredData)}
        actions={
          <>
            {headerActions}
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={handleCreate}
              >
                Add New
              </Button>
            )}
          </>
        }
        emptyMessage={emptyMessage}
      />

      {/* Form Dialog */}
      <FormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? `Edit ${crudConfig.entityName || 'Item'}` : `Add New ${crudConfig.entityName || 'Item'}`}
        fields={fields}
        initialData={editing}
        onSubmit={wrappedHandleFormSubmit}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={`Delete ${crudConfig.entityName || 'Item'}`}
        message={`Are you sure you want to delete this ${crudConfig.entityName?.toLowerCase() || 'item'}? This action cannot be undone.`}
        itemName={itemToDelete?.name || itemToDelete?.title || ''}
      />
    </Box>
  );
};

export default CRUDPage;
